import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/ui/Modal";
import { CustomButton } from "@/components/ui/Buttons";
import {
  useAcceptProjectRequestMutation,
  useGetProjectRequestDetailsQuery,
  useRejectProjectRequestMutation,
} from "@/redux/feature/projectreq/projectRequestApi";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  projectId: string | null;
};

function formatCountdown(ms: number) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function formatSlot(dateISO?: string, start?: string, end?: string) {
  if (!dateISO || !start || !end) return "—";
  const d = new Date(dateISO);
  // simple readable format (customize later)
  const dateLabel = d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
  return `${dateLabel} • ${start} - ${end}`;
}

export default function ProjectRequestModal({
  isOpen,
  onClose,
  projectId,
}: Props) {
  const {
    data: apiRes,
    isLoading,
    isError,
  } = useGetProjectRequestDetailsQuery(
    { projectRequestId: projectId as string },
    { skip: !isOpen || !projectId },
  );

  const [acceptReq, { isLoading: isAccepting }] =
    useAcceptProjectRequestMutation();
  const [rejectReq, { isLoading: isRejecting }] =
    useRejectProjectRequestMutation();

  const [showDetails, setShowDetails] = useState(false);

  const payload = apiRes?.data; // ✅ your backend response is { data: { projectRequest, order } }
  const projectRequest = payload?.projectRequest;
  const order = payload?.order;

  // Pick first video details (you can enhance later for multiple videos)
  const firstVideo = order?.orderVideos?.[0];
  const script = firstVideo?.scriptDetails;
  const editing = firstVideo?.videoEditingDetails;

  // Countdown based on expiresAt
  const [remainingMs, setRemainingMs] = useState(0);

  useEffect(() => {
    if (!isOpen || !projectRequest?.expiresAt) return;

    const tick = () => {
      const exp = new Date(projectRequest.expiresAt).getTime();
      setRemainingMs(exp - Date.now());
    };

    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [isOpen, projectRequest?.expiresAt]);

  const deliveryLabel = useMemo(() => {
    const slot = projectRequest?.selected_slot;
    return formatSlot(slot?.date, slot?.timeSlot?.start, slot?.timeSlot?.end);
  }, [projectRequest?.selected_slot]);

  const productLabel = useMemo(() => {
    // Your order has featuredProduct: [] currently
    const featured = script?.featuredProduct;
    if (!featured || !Array.isArray(featured) || featured.length === 0)
      return "No product";
    // if product has category/title/name, pick best
    const p0 = featured[0];
    return p0?.category?.title || p0?.category?.label || p0?.name || "Product";
  }, [script?.featuredProduct]);

  const canAct =
    projectRequest?.status === "pending" &&
    remainingMs > 0 &&
    !isAccepting &&
    !isRejecting;

  const onAccept = async () => {
    if (!projectId) return;
    await acceptReq({ projectRequestId: projectId, body: {} }).unwrap();
    onClose();
  };

  const onReject = async () => {
    if (!projectId) return;
    await rejectReq({ projectRequestId: projectId, body: {} }).unwrap();
    onClose();
  };

  return (
    <>
      <Modal
        width="600px"
        isOpen={isOpen}
        setOpen={(v) => (!v ? onClose() : null)}
      >
        <div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                New Video Project Request 🎬
              </h2>
              <p className="mt-2 text-sm md:text-base text-muted-foreground">
                You’ve been shortlisted for this project. Please accept or
                reject within the time limit.
              </p>
            </div>
          </div>

          {/* Loading / error */}
          {isLoading && (
            <p className="mt-6 text-sm text-muted-foreground">
              Loading request...
            </p>
          )}
          {!isLoading && isError && (
            <p className="mt-6 text-sm text-destructive">
              Could not load request details. Please refresh.
            </p>
          )}

          {/* Content */}
          {!isLoading && !isError && payload && (
            <>
              {/* Top summary */}
              <div className="mt-6 rounded-xl border border-border p-4 bg-card">
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">
                      Project:
                    </span>{" "}
                    {order?.projectName ?? "—"}{" "}
                    <span className="text-muted-foreground">
                      ({order?.projectId ?? "—"})
                    </span>
                  </p>

                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">Video:</span>{" "}
                    {script?.titleOfTheVideo ?? "—"}
                  </p>

                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="px-3 py-1 rounded-full border border-border text-xs">
                      Delivery: {script?.deliverySlot?.title ?? "—"}
                    </span>
                    <span className="px-3 py-1 rounded-full border border-border text-xs">
                      Length: {script?.videoLength?.label ?? "—"}
                    </span>
                    <span className="px-3 py-1 rounded-full border border-border text-xs">
                      Language:{" "}
                      {script?.language?.title ?? script?.language?.id ?? "—"}
                    </span>
                    <span className="px-3 py-1 rounded-full border border-border text-xs">
                      Region: {script?.regionPreference?.title ?? "—"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Key fields */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Expected Delivery Slot
                  </p>
                  <div className="rounded-xl border border-border px-4 py-3">
                    <span className="font-semibold text-foreground">
                      {deliveryLabel}
                    </span>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-2">Product</p>
                  <div className="rounded-xl border border-border px-4 py-3 flex items-center gap-3">
                    <span className="h-3.5 w-3.5 rounded-full bg-muted inline-block" />
                    <span className="font-semibold text-foreground">
                      {productLabel}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick extra info */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-border p-4">
                  <p className="text-sm text-muted-foreground mb-1">
                    Shoot Format
                  </p>
                  <p className="font-semibold text-foreground">
                    {editing?.format ?? "—"}
                  </p>
                </div>

                <div className="rounded-xl border border-border p-4">
                  <p className="text-sm text-muted-foreground mb-1">
                    Background
                  </p>
                  <p className="font-semibold text-foreground">
                    {editing?.background ?? "—"}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-7 flex flex-col md:flex-row gap-3 md:items-center">
                <CustomButton
                  onClick={onReject}
                  variant="outline"
                  className="border-destructive text-destructive hover:bg-destructive/10"
                  disabled={!canAct}
                >
                  {isRejecting ? "Rejecting..." : "Reject"}
                </CustomButton>

                <button
                  onClick={onAccept}
                  disabled={!canAct}
                  className="w-full md:flex-1 rounded-xl px-5 py-3 font-semibold text-white bg-green-700 hover:bg-green-800 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {isAccepting ? "Accepting..." : "Accept Project"}
                  <span className="bg-white text-orange-600 font-semibold rounded-full px-3 py-1 text-sm">
                    {formatCountdown(remainingMs)} remaining
                  </span>
                </button>
              </div>

              {/* View details button */}
              <div className="mt-4">
                <CustomButton
                  onClick={() => setShowDetails(true)}
                  variant="outline"
                  className="w-full"
                  disabled={!order}
                >
                  View full project details
                </CustomButton>
              </div>

              {/* Note */}
              <div className="mt-6 rounded-xl bg-muted/40 border border-border p-4">
                <p className="text-sm font-semibold text-foreground mb-1">
                  PLEASE NOTE
                </p>
                <p className="text-sm text-muted-foreground">
                  To ensure fast and reliable delivery for clients, we
                  prioritize creators who regularly accept and complete
                  projects.
                </p>
              </div>

              {/* Expired state */}
              {projectRequest?.status === "pending" && remainingMs <= 0 && (
                <p className="mt-4 text-sm text-destructive">
                  This request has expired. Please refresh your dashboard.
                </p>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* 2nd modal for full details */}
      <Modal width="750px" isOpen={showDetails} setOpen={setShowDetails}>
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            Project Details
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Full breakdown of the video requirements.
          </p>

          <div className="mt-5 space-y-4">
            <div className="rounded-xl border border-border p-4">
              <p className="text-sm text-muted-foreground mb-1">Script Title</p>
              <p className="font-semibold">{script?.scriptTitle ?? "—"}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                {script?.scriptDesc?.trim()
                  ? script.scriptDesc
                  : "No script description provided yet."}
              </p>
            </div>

            <div className="rounded-xl border border-border p-4">
              <p className="text-sm text-muted-foreground mb-1">
                Actor Preferences
              </p>
              <p className="text-sm text-foreground">
                Role:{" "}
                <span className="font-medium">
                  {firstVideo?.actorDetails?.[0]?.actorRole ?? "—"}
                </span>
              </p>
              <p className="text-sm text-foreground">
                Gender:{" "}
                <span className="font-medium">
                  {firstVideo?.actorDetails?.[0]?.gender ?? "—"}
                </span>
              </p>
              <p className="text-sm text-foreground">
                Age:{" "}
                <span className="font-medium">
                  {firstVideo?.actorDetails?.[0]?.age?.label ?? "—"}
                </span>
              </p>
            </div>

            <div className="rounded-xl border border-border p-4">
              <p className="text-sm text-muted-foreground mb-1">
                Video Editing Notes
              </p>
              <p className="text-sm text-foreground">
                {editing?.additionalNotes?.trim()
                  ? editing.additionalNotes
                  : "No additional editing notes."}
              </p>
            </div>

            <div className="flex justify-end">
              <CustomButton onClick={() => setShowDetails(false)}>
                Close
              </CustomButton>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
